import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { Check } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Overline } from "@/components/public/ui";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import useSeo from "@/lib/useSeo";

export default function DynamicForm() {
  const { slug } = useParams();
  const [form, setForm] = useState(null);
  const [values, setValues] = useState({});
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  useSeo({ title: form?.name });

  useEffect(() => { api.get(`/forms/${slug}`).then(({ data }) => setForm(data)).catch(() => setForm(false)); }, [slug]);
  const set = (name) => (e) => setValues({ ...values, [name]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try { await api.post(`/forms/${slug}/submit`, { data: values }); setDone(true); toast.success("Formulaire envoyé !"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  if (form === null) return <div className="max-w-xl mx-auto px-6 py-32 text-center text-slate-400">Chargement…</div>;
  if (form === false) return <div className="max-w-xl mx-auto px-6 py-32 text-center text-slate-500">Formulaire introuvable.</div>;
  if (done) return (
    <div className="max-w-xl mx-auto px-6 py-28 text-center">
      <div className="w-16 h-16 rounded-full bg-[#4CB944]/15 grid place-items-center mx-auto mb-6"><Check className="w-8 h-8 text-[#4CB944]" /></div>
      <h1 className="text-3xl font-bold text-slate-900 font-head">Merci !</h1>
      <p className="mt-3 text-slate-600">Votre message a bien été envoyé. Nous vous recontacterons rapidement.</p>
    </div>
  );

  return (
    <div>
      <section className="bg-gradient-to-br from-[#0F2A6B] to-[#0A1F52] relative grain">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 py-16 relative">
          <Overline><span className="text-[#4CB944]">Formulaire</span></Overline>
          <h1 className="mt-3 text-3xl sm:text-4xl font-bold text-white font-head">{form.name}</h1>
          {form.description && <p className="mt-3 text-slate-300">{form.description}</p>}
        </div>
      </section>
      <section className="max-w-2xl mx-auto px-4 sm:px-6 py-12">
        <form onSubmit={submit} data-testid="dynamic-form" className="p-8 rounded-2xl bg-white border border-slate-200 shadow-sm space-y-5">
          {(form.fields || []).map((f) => (
            <div key={f.name}>
              <label className="text-sm font-medium text-slate-700">{f.label}{f.required && " *"}</label>
              {f.type === "textarea"
                ? <Textarea required={f.required} value={values[f.name] || ""} onChange={set(f.name)} rows={4} className="mt-1.5" />
                : f.type === "select"
                ? <select required={f.required} value={values[f.name] || ""} onChange={set(f.name)} className="w-full mt-1.5 px-3 py-2 rounded-lg border border-slate-200 text-sm"><option value="">— Choisir —</option>{(f.options || []).map((o) => <option key={o} value={o}>{o}</option>)}</select>
                : <Input type={f.type === "email" ? "email" : f.type === "number" ? "number" : "text"} required={f.required} value={values[f.name] || ""} onChange={set(f.name)} className="mt-1.5" />}
            </div>
          ))}
          <button type="submit" disabled={loading} data-testid="dynamic-form-submit" className="px-6 py-3 rounded-full bg-[#0F2A6B] text-white font-semibold hover:bg-[#0A1F52] disabled:opacity-60">{loading ? "Envoi…" : (form.submit_label || "Envoyer")}</button>
        </form>
      </section>
    </div>
  );
}
